import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";

const API = process.env.REACT_APP_API_URL || "/api";

function EditCompany() {
  const { companyId } = useParams();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    company_name: "",
    company_type: "",
    location: "",
    logo_data: "",
    status: "active",
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const token = localStorage.getItem("token");

  /* ================= FETCH COMPANY ================= */
  useEffect(() => {
    const fetchCompany = async () => {
      try {
        const res = await fetch(`${API}/companies/${companyId}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!res.ok) throw new Error("Failed to fetch company");

        const company = await res.json();

        setForm({
          company_name: company.company_name || "",
          company_type: company.company_type || "",
          location: company.location || "",
          logo_data: company.logo_data || "",
          status: company.status || "active",
        });

      } catch (err) {
        console.error(err);
        setError("Failed to load company");
      } finally {
        setLoading(false);
      }
    };

    fetchCompany();
  }, [companyId, token]);

  /* ================= HANDLE CHANGE ================= */
  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setError("");
  };

  const handleLogoChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!/^image\/(png|jpeg|webp|svg\+xml)$/.test(file.type) || file.size > 2 * 1024 * 1024) {
      setError("Choose a PNG, JPEG, WebP, or SVG logo under 2 MB.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setForm((current) => ({ ...current, logo_data: String(reader.result || "") }));
    reader.readAsDataURL(file);
  };

  /* ================= SUBMIT ================= */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");

    try {
      const res = await fetch(`${API}/companies/${companyId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(form),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.message || "Update failed");
      }

      navigate("/dashboard/companies");

    } catch (err) {
      console.error(err);
      setError(err.message || "Failed to update company");
    } finally {
      setSaving(false);
    }
  };

  /* ================= LOADING ================= */
  if (loading) {
    return (
      <div className="p-6 text-sm text-gray-500">
        Loading company details...
      </div>
    );
  }

  return (
    <div className="p-6 max-w-2xl mx-auto">

      {/* HEADER */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-800">
          Edit Company
        </h1>
        <p className="text-sm text-slate-500">
          Update organization details
        </p>
      </div>

      {/* FORM CARD */}
      <div className="bg-white p-8 rounded-2xl shadow-sm border">

        {/* ERROR */}
        {error && (
          <div className="mb-4 p-3 text-sm text-red-600 bg-red-50 rounded-lg">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-5">

          <Input
            label="Company Name"
            name="company_name"
            value={form.company_name}
            onChange={handleChange}
            required
          />

          <Input
            label="Company Type"
            name="company_type"
            value={form.company_type}
            onChange={handleChange}
          />

          <Input
            label="Location"
            name="location"
            value={form.location}
            onChange={handleChange}
          />

          <div>
            <label className="block text-sm font-medium mb-2 text-slate-700">Company Logo</label>
            <div className="flex items-center gap-3">
              {form.logo_data && <img src={form.logo_data} alt="Company logo preview" className="h-12 w-12 rounded border border-slate-200 object-contain p-1" />}
              <input type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" onChange={handleLogoChange} className="block w-full text-sm" />
              {form.logo_data && <button type="button" onClick={() => setForm((current) => ({ ...current, logo_data: "" }))} className="text-sm text-red-600">Remove</button>}
            </div>
            <p className="mt-1 text-xs text-slate-500">PNG, JPEG, WebP, or SVG. Maximum 2 MB.</p>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2 text-slate-700">
              Status
            </label>
            <select
              name="status"
              value={form.status}
              onChange={handleChange}
              className="w-full border border-slate-300 px-4 py-2.5 rounded-xl focus:ring-2 focus:ring-green-500"
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>

          {/* ACTIONS */}
          <div className="flex justify-end gap-3 pt-4">

            <button
              type="button"
              onClick={() => navigate("/dashboard/companies")}
              className="px-5 py-2 rounded-lg border text-slate-600 hover:bg-slate-100"
            >
              Cancel
            </button>

            <button
              type="submit"
              disabled={saving}
              className={`px-6 py-2 rounded-lg font-semibold text-white ${
                saving
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-green-600 hover:bg-green-700"
              }`}
            >
              {saving ? "Updating..." : "Update Company"}
            </button>

          </div>

        </form>
      </div>
    </div>
  );
}

/* ================= INPUT ================= */
const Input = ({ label, ...props }) => (
  <div>
    <label className="block text-sm font-medium mb-2 text-slate-700">
      {label}
    </label>

    <input
      {...props}
      className="w-full border border-slate-300 px-4 py-2.5 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500"
    />
  </div>
);

export default EditCompany;
